import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { useMediaQuery } from "@mui/material";
import { noop, BREAKPOINT_DESKTOP } from "../../../../constants/constants";
import {
  useGetPageFromPath,
  useEventListener,
} from "utils/customHooks/genericHooks";
import { useTimelineTooltipStore } from "./timelineTooltipStore";
import { unselectAllSegments } from "../ProductionSegments/MouseMoveHandler";

/** close the TimelineTooltip */
export function useHandleCloseModal() {
  const [, setTimelineTooltipStore] = useTimelineTooltipStore();

  return useCallback(() => {
    setTimelineTooltipStore(p => ({
      ...p,
      modal: null,
      selectedProdSegment: null,
      isModalPinned: false,
    }));
    unselectAllSegments();
  }, [setTimelineTooltipStore]);
}

/** close the TimelineTooltip unless it's pinned */
export function useHandleCloseModalUnlessPinned() {
  const [{ isModalPinned, modal }] = useTimelineTooltipStore();
  const handleCloseModal = useHandleCloseModal();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useCallback(isModalPinned || !modal ? noop : handleCloseModal, [
    isModalPinned,
    modal,
    handleCloseModal,
  ]);
}

/** track the position of the top of the #timeAxisSvg
 * so we can flip the TimelineTooltip to downward-facing
 * when there's not enough room above it
 */
export function useTrackTimeAxisTop() {
  const [timeAxisTop, setTimeAxisTop] = useState(0);
  const timerRef = useRef(null as number | null);

  const isDesktopOrLarger = useMediaQuery(
    `(min-width: ${BREAKPOINT_DESKTOP}px)`
  );
  const { isHubletPage } = useGetPageFromPath();
  const isLeftRightHubletPage = isDesktopOrLarger && isHubletPage;

  const handleTrackTimeAxisTop = () => {
    const timeAxisSvg = document.getElementById("timeAxisSvg");
    if (!timeAxisSvg) {
      return;
    }
    const timeAxisRect = timeAxisSvg.getBoundingClientRect();
    setTimeAxisTop(timeAxisRect.top);
  };

  // scrolling fires many events at once,
  // but we only need to track the position when scrolling is done.
  // so, track the position after a timeout, and clear all previous timers so we only do it once per scroll.
  const handleTrackTimeAxisTopAfterTimeout = () => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
    }

    timerRef.current = window.setTimeout(handleTrackTimeAxisTop, 500);
  };

  useLayoutEffect(handleTrackTimeAxisTop, []);

  const hubletPageRightSide = document.querySelector(".datepickerAndCharts");

  useEventListener(
    "scroll",
    handleTrackTimeAxisTopAfterTimeout,
    isLeftRightHubletPage ? (hubletPageRightSide as HTMLElement) : window
  );

  return timeAxisTop;
}
